import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "features/userSlice";
import { Link, useHistory, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db, functions, httpsCallable } from "/Users/michaeltucker/Documents/coding-projects/client/src/firebase.js";
import { persistMetaSubscriptionCheckoutIntent } from "utils/metaPixel";
import { FULLSTORY_ELEMENTS } from "utils/fullstoryElements";
import { DEFAULT_PRICING_DATA } from "utils/pricingDefaults";
import { clearAdminShellBodyClasses } from "utils/adminShellBodyClasses";
import {
  isFallWinbackDiscountCheckoutOpen,
  isFallWinbackPreviewActive,
} from "utils/campaignEntitlements";

const STRIPE_PUBLISHABLE_KEY =
  "pk_live_51Nu711Ecrsj3dDGxkLAjXv80asu2XZSy93BwoTvLzMJZIS40tt1BSVy6yy0yTZzvcsyLKgyyCeIcOitrB78uCvvn00OlAZth85";

const MONTHLY_PRICING_TABLE_ID = "prctbl_1Qpwj4Ecrsj3dDGxfMjv9P5O";
const YEARLY_PRICING_TABLE_ID = "prctbl_1QpwIQEcrsj3dDGxQj6iv0aV";
const RENEW_BUY_BUTTON_ID = "buy_btn_1PiLdHEcrsj3dDGxqfH9U2fO";
const FALL_WINBACK_ANNUAL_PRICE = "29.99";

function createFallWinbackCheckoutCallable() {
  return httpsCallable(functions, "createFallWinbackCheckoutSession");
}

function UnifiedPricingCard({ pricingData, clientReferenceId, onBillingPeriodChange }) {
  const [billingPeriod, setBillingPeriod] = useState("annual");
  const isMonthly = billingPeriod === "monthly";
  const pricingTableId = isMonthly ? MONTHLY_PRICING_TABLE_ID : YEARLY_PRICING_TABLE_ID;

  const handleBillingPeriodChange = (nextBillingPeriod) => {
    setBillingPeriod(nextBillingPeriod);
    onBillingPeriodChange?.(nextBillingPeriod);
  };

  return (
    <article className="pricing-unified-card card">
      <div className="pricing-billing-toggle" role="tablist" aria-label="Billing period">
        <button
          type="button"
          role="tab"
          aria-selected={isMonthly}
          className={`pricing-billing-toggle-option${isMonthly ? " is-active" : ""}`}
          onClick={() => handleBillingPeriodChange("monthly")}
          data-fs-element={FULLSTORY_ELEMENTS.billing.selectMonthly}
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isMonthly}
          className={`pricing-billing-toggle-option${!isMonthly ? " is-active" : ""}`}
          onClick={() => handleBillingPeriodChange("annual")}
          data-fs-element={FULLSTORY_ELEMENTS.billing.selectAnnual}
        >
          Annually
          <span className="pricing-billing-save-badge">Save {pricingData.savings}</span>
        </button>
      </div>

      <p className="pricing-unified-price">
        {isMonthly ? (
          <>
            ${pricingData.monthlyPrice}{" "}
            <span className="pricing-unified-price-period">/ month</span>
          </>
        ) : (
          <>
            ${pricingData.yearlyPrice}{" "}
            <span className="pricing-unified-price-period">/ year</span>
          </>
        )}
      </p>

      {!isMonthly ? (
        <p className="pricing-unified-savings-note">
          You save {pricingData.savings} compared to monthly!
        </p>
      ) : null}

      <div className="pricing-unified-stripe" key={pricingTableId}>
        <stripe-pricing-table
          pricing-table-id={pricingTableId}
          publishable-key={STRIPE_PUBLISHABLE_KEY}
          client-reference-id={clientReferenceId}
          data-fs-element={isMonthly
            ? FULLSTORY_ELEMENTS.billing.startMonthlySubscription
            : FULLSTORY_ELEMENTS.billing.startAnnualSubscription}
        />
      </div>
      <p className="pricing-unified-fallback">
        <span>Stripe checkout not loading?</span>
        <Link
          to="/pricing/checkout-help"
          data-fs-element={FULLSTORY_ELEMENTS.errorRecovery.retry}
        >
          See checkout troubleshooting links.
        </Link>
      </p>
    </article>
  );
}

function FallWinbackPricingCard({
  pricingData,
  clientReferenceId,
  onAnnualCheckout,
  offerLoading,
  offerError,
}) {
  return (
    <article className="pricing-unified-card card pricing-unified-card--single">
      <p className="pricing-unified-savings-note">
        Welcome-back preview offer: save 25% on your first year.
      </p>
      <p className="pricing-unified-price">
        ${FALL_WINBACK_ANNUAL_PRICE}{" "}
        <span className="pricing-unified-price-period">first year</span>
      </p>
      <p className="pricing-unified-savings-note">
        Regular annual price ${pricingData.yearlyPrice}. Renews at the regular annual price unless canceled.
      </p>
      {offerError ? (
        <p className="pricing-unified-fallback" role="alert">{offerError}</p>
      ) : null}
      <button
        type="button"
        className="btn design-system-primary-solid pricing-manage-subscription-link"
        onClick={onAnnualCheckout}
        disabled={offerLoading}
        data-fs-element={FULLSTORY_ELEMENTS.billing.startAnnualSubscription}
      >
        {offerLoading ? "Opening checkout..." : "Get my first year for $29.99"}
      </button>
      <div className="pricing-unified-stripe" key={MONTHLY_PRICING_TABLE_ID}>
        <p className="pricing-unified-savings-note">Prefer monthly?</p>
        <stripe-pricing-table
          pricing-table-id={MONTHLY_PRICING_TABLE_ID}
          publishable-key={STRIPE_PUBLISHABLE_KEY}
          client-reference-id={clientReferenceId}
          data-fs-element={FULLSTORY_ELEMENTS.billing.startMonthlySubscription}
        />
      </div>
    </article>
  );
}

function AlreadySubscribedCard() {
  return (
    <article className="pricing-unified-card card pricing-unified-card--single pricing-already-subscribed-card">
      <h2>You already have an active subscription</h2>
      <p>
        Manage invoices, payment details, coupon codes, pauses, and cancellation from your account page.
      </p>
      <Link
        to="/account/subscription"
        className="btn design-system-primary-solid pricing-manage-subscription-link"
        data-fs-element={FULLSTORY_ELEMENTS.billing.manageSubscription}
      >
        Manage Subscription
      </Link>
    </article>
  );
}

function PricingTable() {
  const user = useSelector(selectUser);
  const history = useHistory();
  const location = useLocation();
  const isNonRenew = location.pathname.includes("non-renew");
  const [pricingData, setPricingData] = useState(DEFAULT_PRICING_DATA);
  const [billingPeriod, setBillingPeriod] = useState("annual");
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState("");
  const showFallWinbackOffer = isFallWinbackPreviewActive(user)
    && isFallWinbackDiscountCheckoutOpen(user);

  useLayoutEffect(() => {
    clearAdminShellBodyClasses();
  }, []);

  const handleFallWinbackAnnualCheckout = async () => {
    if (offerLoading) return;
    setOfferLoading(true);
    setOfferError("");

    try {
      const result = await createFallWinbackCheckoutCallable()({
        returnOrigin: window.location.origin,
      });
      if (!result?.data?.url) {
        throw new Error("Checkout URL was not returned.");
      }
      window.location.assign(result.data.url);
    } catch (error) {
      console.error("Failed to open welcome-back checkout:", error);
      setOfferError(error?.message || "We could not open the welcome-back checkout. Please try again.");
    } finally {
      setOfferLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid || user.premium) {
      return;
    }

    void persistMetaSubscriptionCheckoutIntent({
      uid: user.uid,
      billingPeriod,
      monthlyPrice: Number(pricingData.monthlyPrice),
      yearlyPrice: Number(pricingData.yearlyPrice),
    });
  }, [billingPeriod, pricingData.monthlyPrice, pricingData.yearlyPrice, user?.premium, user?.uid]);

  useEffect(() => {
    if (!user && !isNonRenew) {
      history.replace("/auth/login#pricing");
    }
  }, [user, history, isNonRenew]);

  useEffect(() => {
    let isMounted = true;

    const fetchPricingData = async () => {
      try {
        const docRef = doc(db, "dynamicData", "tqZ8VusxvnaBEQRoDTeW");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && isMounted) {
          const remotePricing = docSnap.data()?.pricingData;
          if (remotePricing) {
            setPricingData({
              monthlyPrice: remotePricing.monthlyPrice ?? DEFAULT_PRICING_DATA.monthlyPrice,
              yearlyPrice: remotePricing.yearlyPrice ?? DEFAULT_PRICING_DATA.yearlyPrice,
              savings: remotePricing.savings ?? DEFAULT_PRICING_DATA.savings,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching pricing data:", error);
      }
    };

    fetchPricingData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isNonRenew) {
    return (
      <div className="pricing-page pricing-page--compact">
        <div className="pricing-page-shell pricing-page-shell--narrow">
          <header className="pricing-page-header">
            <h1 className="pricing-page-title">
              {user?.premium ? "You're already subscribed" : "Renew your subscription"}
            </h1>
            <p className="pricing-page-lead">
              {user?.premium
                ? "Your account already has premium access."
                : "Pick up right where you left off with Lesson Launchpad."}
            </p>
          </header>
          {user?.premium ? (
            <AlreadySubscribedCard />
          ) : (
            <article className="pricing-unified-card card pricing-unified-card--single">
              <div className="pricing-unified-stripe">
                <stripe-buy-button
                  buy-button-id={RENEW_BUY_BUTTON_ID}
                  publishable-key={STRIPE_PUBLISHABLE_KEY}
                  data-fs-element={FULLSTORY_ELEMENTS.billing.reactivateSubscription}
                />
              </div>
            </article>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (user.premium) {
    return (
      <div className="pricing-page">
        <div className="pricing-page-shell pricing-page-shell--unified">
          <header className="pricing-page-header">
            <h1 className="pricing-page-title">You're already subscribed</h1>
            <p className="pricing-page-lead">
              <span>Your account already has premium access.</span>
              <span>Use your subscription page for billing changes.</span>
            </p>
          </header>
          <div className="pricing-page-unified-wrap">
            <AlreadySubscribedCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-page-shell pricing-page-shell--unified">
        <header className="pricing-page-header">
          <h1 className="pricing-page-title">Pricing</h1>
          <p className="pricing-page-lead">
            <span>One plan with full access.</span>
            <span>Choose monthly or annual billing below.</span>
          </p>
        </header>
        <div className="pricing-page-unified-wrap">
          {showFallWinbackOffer ? (
            <FallWinbackPricingCard
              pricingData={pricingData}
              clientReferenceId={user.uid}
              onAnnualCheckout={handleFallWinbackAnnualCheckout}
              offerLoading={offerLoading}
              offerError={offerError}
            />
          ) : (
            <UnifiedPricingCard
              pricingData={pricingData}
              clientReferenceId={user.uid}
              onBillingPeriodChange={setBillingPeriod}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default PricingTable;
